import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { IUserLocalStorageData } from '@models/localstorage.model';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  readonly #stateService = inject(StateService);
  readonly #router = inject(Router);

  // Signal privado para el estado interno
  readonly #currentUser = signal<IUserLocalStorageData | null>(null);

  // Signals públicos que el resto de la app puede consumir de forma segura
  public readonly currentUser = this.#currentUser.asReadonly();
  public readonly isAuthenticated = computed(() => !!this.#currentUser());
  public readonly userToken = computed(() => this.#currentUser()?.token);

  constructor() {
    // Al iniciar, intenta recuperar la sesión desde localStorage
    const savedData = localStorage.getItem('data');
    if (savedData) {
      try {
        const userData: IUserLocalStorageData = JSON.parse(savedData);
        if (this.isTokenValid(userData.token)) {
          this.#currentUser.set(userData);
        } else {
          localStorage.removeItem('data'); // Limpia si el token ha expirado
        }
      } catch (error) {
        localStorage.removeItem('data');
      }
    }
  }

  /**
   * Inicia una nueva sesión de usuario.
   * @param userData Los datos de sesión del usuario, incluyendo el token.
   */
  public startSession(userData: IUserLocalStorageData): void {
    if (this.isTokenValid(userData.token)) {
      localStorage.setItem('data', JSON.stringify(userData));
      this.#currentUser.set(userData);
    }
  }

  /**
   * @description Termina la sesión actual del usuario, limpiando el estado y redirigiendo al login.
   * @returns {void}
   */
  public endSession(): void {
    this.#currentUser.set(null);
    this.#stateService.clearCache();
    localStorage.removeItem('data');
    this.#router.navigate(['auth', 'login']);
  }

  /**
   * Verifica si un token JWT es válido (no ha expirado).
   * @param token El token JWT a validar.
   * @returns {boolean} `true` si el token es válido, `false` en caso contrario.
   */
  private isTokenValid(token: string | undefined): boolean {
    if (!token) return false;
    try {
      // Decodifica la parte del payload del token (la segunda parte)
      const payload = JSON.parse(atob(token.split('.')[1]));

      // 'exp' es un timestamp en segundos, lo convertimos a milisegundos
      const expiry = payload.exp * 1000;

      // Comprueba si la fecha de expiración es en el futuro
      return Date.now() < expiry;
    } catch (e) {
      // Si hay cualquier error al decodificar, el token es inválido
      return false;
    }
  }
}
